import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMapPin, FiCamera, FiHeart, FiPhone, FiStar } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { generateSlug } from '../utils/slugify';
import { getImageUrl, getDefaultPropertyImage } from '../utils/imageUtils';

const PropertyCard = ({ property, layout = 'vertical' }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const { user, userFavorites, toggleFavorite } = useContext(AuthContext);

  const agentName = property.postedBy?.name || 'Real Properties';
  const agentInitials = agentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  const isWealth = property.isWealthProperty;
  
  let imgUrl = getDefaultPropertyImage(property.propertyType, property.subCategory);
  if (property.images && property.images.length > 0) {
    imgUrl = getImageUrl(property.images[currentImgIndex], isWealth);
  }



  const isFavorite = userFavorites?.some(fav => 
    typeof fav === 'object' ? fav._id === property._id : fav === property._id
  );

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    await toggleFavorite(property._id);
  };

  const handleContactClick = (e) => {
    e.stopPropagation();
    navigate(`/property/${generateSlug(property.title)}/${property._id}`);
  };

  const formatPrice = (price) => {
    if (!price) return 'On Request';
    if (price >= 10000000) return `₹ ${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹ ${(price / 100000).toFixed(1)} L`;
    return `₹ ${price.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const diffTime = Math.abs(new Date() - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Posted today';
    if (diffDays === 2) return 'Posted yesterday';
    if (diffDays <= 7) return `Posted ${diffDays - 1} days ago`;
    
    return `Posted on ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (layout === 'horizontal') {
    return (
      <div 
        className="bg-white rounded-xl border border-gray-200 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 relative group flex flex-col md:flex-row overflow-hidden w-full"
        onClick={handleContactClick}
      >
        {/* Left Side: Image Slider */}
        <div className="relative w-full md:w-[320px] h-[220px] md:h-auto flex-shrink-0 overflow-hidden">
          <div className="absolute top-2 left-2 z-10 flex gap-2 flex-col items-start">
            {property.isFeatured && (
              <span className="bg-yellow-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider flex items-center gap-1">
                <FiStar size={10} className="fill-white" /> Featured
              </span>
            )}
            {(property.status === 'sold' || property.status === 'expired') && <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider">Sold Out</span>}
            {property.status === 'approved' && <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1"><span className="w-2 h-2 bg-white rounded-full inline-block"></span> Verified</span>}
            {property.for === 'Rent' ? (
              <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Rent</span>
            ) : (
              <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Sale</span>
            )}
          </div>
          
          {/* Favorite Button */}
          <button 
            onClick={handleFavoriteClick}
            className="absolute z-10 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
            style={{ top: '10px', right: '10px' }}
          >
            <FiHeart className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-500"} />
          </button>

          <img 
            src={imgError ? 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80&fm=webp' : imgUrl} 
            onError={() => setImgError(true)}
            alt={property.title} 
            className={`absolute inset-0 w-full h-full object-cover ${(property.status === 'sold' || property.status === 'expired') ? 'grayscale opacity-80' : ''}`} 
            style={{ transition: 'opacity 0.5s ease-in-out' }} 
          />
          {(!property.images || property.images.length === 0) && !property.coverImage && !property.photo && (
            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center z-20">
              <button 
                onClick={(e) => { e.stopPropagation(); handleContactClick(e); }} 
                className="bg-white text-primary px-3 py-1.5 rounded font-bold text-xs md:text-sm shadow-lg flex items-center gap-1.5 hover:bg-gray-100 transition-colors"
              >
                <FiCamera /> Request Image
              </button>
            </div>
          )}
          <div className="absolute bottom-2 right-2 z-10 bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded flex items-center gap-1">
            <FiCamera size={12} /> {property.images?.length || 0}
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="p-4 md:p-5 flex flex-col flex-grow">
          <div className="mb-3">
            <h3 className="font-bold text-lg md:text-xl text-gray-900 mb-1 line-clamp-1 hover:text-primary transition-colors">
              <Link to={`/property/${generateSlug(property.title)}/${property._id}`}>{property.title}</Link>
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1 line-clamp-1">
              <FiMapPin size={14} className="flex-shrink-0" /> 
              {property.address?.line1 ? `${property.address.line1}, ` : ''}{property.address?.city}
            </p>
          </div>

          <div className="mb-4">
            {(() => {
              const total = Number(property.price?.total);
              const areaVal = Number(property.area?.value);
              const areaUnit = property.area?.unit || 'Sq Yards';
              const perUnitValue = property.price?.perUnit ? Number(property.price.perUnit) : (total && areaVal ? Math.round(total / areaVal) : null);
              
              if (!areaVal && !total) return <div className="font-bold text-lg text-gray-900 mb-2">On Request</div>;
              
              const parts = [];
              if (areaVal) parts.push(<span key="area" className="text-primary font-bold">{areaVal} {areaUnit}</span>);
              if (perUnitValue) parts.push(<span key="perUnit" className="text-gray-700 font-medium">@ ₹{perUnitValue.toLocaleString('en-IN')}/{areaUnit.replace(/s$/i, '')}</span>);
              parts.push(<span key="total" className="text-gray-900 font-bold">₹{total ? total.toLocaleString('en-IN') : 'On Request'}</span>);
              
              return (
                <div className="flex items-center gap-2 flex-wrap mb-3 text-[15px]">
                  {parts.length > 0 ? parts.reduce((prev, curr, i) => [prev, <span key={`sep-${i}`} className="text-gray-400">-</span>, curr]) : null}
                </div>
              );
            })()}

            {(property.specifications?.bedrooms || property.condition) && (
              <div className="flex flex-wrap gap-x-8 gap-y-2 pt-2 border-t border-gray-100">
                {property.specifications?.bedrooms && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-0.5">Configuration</p>
                    <div className="font-semibold text-[14px] text-gray-800">
                      {property.specifications.bedrooms} BHK
                    </div>
                  </div>
                )}
                
                {property.condition && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-0.5">Status</p>
                    <div className="font-semibold text-[14px] text-gray-800 capitalize">
                      {property.condition}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mb-4 flex-grow">
            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
              {property.description}
            </p>
          </div>

          <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs rounded-full w-8 h-8 flex-shrink-0">
                {agentInitials}
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-900 block truncate max-w-[120px] md:max-w-[200px]" title={agentName}>
                  {agentName}
                </span>
                <div className="text-[10px] text-gray-500 flex items-center gap-1">
                  <span>Platform Verified</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button onClick={handleContactClick} className="px-4 py-2 text-sm font-semibold text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors hidden sm:block">
                Details
              </button>
              <button onClick={handleContactClick} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg flex items-center gap-1.5 hover:bg-orange-600 transition-colors shadow-sm">
                <FiPhone size={14} /> Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vertical Layout (Rich Grid Style)
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 relative group flex flex-col overflow-hidden w-full h-full"
      onClick={handleContactClick}
    >
      {/* Top Side: Image Slider */}
      <div className="relative w-full h-[240px] flex-shrink-0 overflow-hidden bg-gray-100">
        <div className="absolute top-2 left-2 z-10 flex gap-2 flex-col items-start">
          {property.isFeatured && (
            <span className="bg-yellow-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider flex items-center gap-1">
              <FiStar size={10} className="fill-white" /> Featured
            </span>
          )}
          {(property.status === 'sold' || property.status === 'expired') && <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider">Sold Out</span>}
          {property.status === 'approved' && <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1"><span className="w-2 h-2 bg-white rounded-full inline-block"></span> Verified</span>}
          {property.for === 'Rent' ? (
            <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Rent</span>
          ) : (
            <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">For Sale</span>
          )}
        </div>
        
        {/* Favorite Button */}
        <button 
          onClick={handleFavoriteClick}
          className="absolute z-10 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
          style={{ top: '10px', right: '10px' }}
        >
          <FiHeart className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-500"} />
        </button>

        <img 
          src={imgError ? 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80&fm=webp' : imgUrl} 
          onError={() => setImgError(true)}
          alt={property.title} 
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${(property.status === 'sold' || property.status === 'expired') ? 'grayscale opacity-80' : ''}`} 
        />
        {(!property.images || property.images.length === 0) && !property.coverImage && !property.photo && (
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center z-20">
            <button 
              onClick={(e) => { e.stopPropagation(); handleContactClick(e); }} 
              className="bg-white text-primary px-3 py-1.5 rounded font-bold text-xs md:text-sm shadow-lg flex items-center gap-1.5 hover:bg-gray-100 transition-colors"
            >
              <FiCamera /> Request Image
            </button>
          </div>
        )}
        <div className="absolute bottom-2 right-2 z-10 bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded flex items-center gap-1">
          <FiCamera size={12} /> {property.images?.length || 0}
        </div>
      </div>

      {/* Bottom Side: Content */}
      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-3">
          <h3 className="font-bold text-lg md:text-xl text-gray-900 mb-1 line-clamp-1 hover:text-primary transition-colors">
            <Link to={`/property/${generateSlug(property.title)}/${property._id}`}>{property.title}</Link>
          </h3>
          <p className="text-sm text-gray-500 flex items-center gap-1 line-clamp-1">
            <FiMapPin size={14} className="flex-shrink-0" /> 
            {property.address?.line1 ? `${property.address.line1}, ` : ''}{property.address?.city}
          </p>
        </div>

        <div className="mb-4">
          {(() => {
            const total = Number(property.price?.total);
            const areaVal = Number(property.area?.value);
            const areaUnit = property.area?.unit || 'Sq Yards';
            const perUnitValue = property.price?.perUnit ? Number(property.price.perUnit) : (total && areaVal ? Math.round(total / areaVal) : null);
            
            if (!areaVal && !total) return <div className="font-bold text-lg text-gray-900 mb-2">On Request</div>;
            
            const parts = [];
            if (areaVal) parts.push(<span key="area" className="text-primary font-bold">{areaVal} {areaUnit}</span>);
            if (perUnitValue) parts.push(<span key="perUnit" className="text-gray-700 font-medium">@ ₹{perUnitValue.toLocaleString('en-IN')}/{areaUnit.replace(/s$/i, '')}</span>);
            parts.push(<span key="total" className="text-gray-900 font-bold">₹{total ? total.toLocaleString('en-IN') : 'On Request'}</span>);
            
            return (
              <div className="flex items-center gap-2 flex-wrap mb-3 text-[15px]">
                {parts.length > 0 ? parts.reduce((prev, curr, i) => [prev, <span key={`sep-${i}`} className="text-gray-400">-</span>, curr]) : null}
              </div>
            );
          })()}

          {(property.specifications?.bedrooms || property.condition) && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 pt-2 border-t border-gray-100">
              {property.specifications?.bedrooms && (
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-0.5">Configuration</p>
                  <div className="font-semibold text-[14px] text-gray-800">
                    {property.specifications.bedrooms} BHK
                  </div>
                </div>
              )}
              
              {property.condition && (
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-0.5">Status</p>
                  <div className="font-semibold text-[14px] text-gray-800 capitalize">
                    {property.condition}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs rounded-full w-8 h-8 flex-shrink-0">
              {agentInitials}
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-900 block truncate max-w-[100px]" title={agentName}>
                {agentName}
              </span>
              <div className="text-[10px] text-gray-500 flex items-center gap-1">
                <span>Platform Verified</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button onClick={handleContactClick} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded flex items-center gap-1.5 hover:bg-orange-600 transition-colors shadow-sm">
              <FiPhone size={12} /> Contact
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
